import { inject, Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { TranslateParams, TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject, filter, Observable, switchMap } from 'rxjs';
import { map, shareReplay, take, tap } from 'rxjs/operators';
import { getLogger } from 'tofe-core';

import { DEFAULT_LOCALE, LANGUAGE_TO_LOCALE_MAP } from './language.constants';
import { LanguageConfigurationService } from './language-configuration.service';

// Local type definition to avoid importing from internal transloco paths
type HashMap<T = unknown> = Record<string, T>;

const logger = getLogger('[Service][Language]');

function updateQueryParam(key: string, value: string) {
  if (!window.location.href) return;
  const currentUrl = window.location.href;
  const url = new URL(currentUrl);
  const searchParams = url.searchParams;
  if (searchParams.get(key) === value) return;
  searchParams.set(key, value);
  url.search = searchParams.toString();
  window.history.replaceState({}, '', url);
}

@Injectable()
export class LanguageService {
  private readonly transloco = inject(TranslocoService);
  private readonly conf = inject(LanguageConfigurationService);

  translateIsReady$ = new BehaviorSubject<boolean>(false);
  current$: Observable<string>;
  private readonly currentSubject = new BehaviorSubject<string>('');

  constructor() {
    this.current$ = this.currentSubject.asObservable().pipe(
      filter((x) => !!x),
      tap((y) => {
        logger.debug(`using ${y}`);
      }),
      switchMap((lang) => {
        logger.debug(`current$ activating transloco`, lang);
        this.transloco.setActiveLang(lang);
        document.documentElement.lang = lang;
        return this.transloco.selectTranslation(lang).pipe(map(() => lang));
      }),
      tap((y) => {
        this.translateIsReady$.next(true);
        Preferences.set({ key: 'tofe-language', value: y });
        updateQueryParam('lang', y);
        logger.debug(`current$`, y);
      }),
      shareReplay(1),
    );
  }

  init(): Observable<string> {
    return this.current$.pipe(take(1));
  }

  getCurrent(): string {
    return this.currentSubject.getValue();
  }

  getCurrentLocale(): string {
    const curr = this.getCurrent();

    // Use the mapping if available
    if (LANGUAGE_TO_LOCALE_MAP[curr]) {
      return LANGUAGE_TO_LOCALE_MAP[curr];
    }

    // Fallback: if not in mapping, validate and create standard format
    if (curr && curr.length >= 2) {
      const lang = curr.toLowerCase();
      const region = curr.toUpperCase();
      const locale = `${lang}-${region}`;

      // Basic validation using Intl.Locale if available
      try {
        if (typeof Intl !== 'undefined' && 'Locale' in Intl) {
          new (Intl as typeof Intl & { Locale: new (locale: string) => unknown }).Locale(locale);
          return locale;
        }
        return locale;
      } catch (error) {
        logger.error(`Invalid locale format for language "${curr}", falling back to ${DEFAULT_LOCALE}`, error);
        return DEFAULT_LOCALE;
      }
    }

    // Ultimate fallback
    logger.error(`No valid language code provided: "${curr}", falling back to ${DEFAULT_LOCALE}`);
    return DEFAULT_LOCALE;
  }

  setCurrent(lang: string) {
    logger.debug(`setCurrent`, lang);
    return this.currentSubject.next(this.filterAvailableLanguage(lang));
  }

  translate<T = string>(key: TranslateParams, params?: HashMap, lang?: string): T {
    return this.transloco.translate(key, params, lang);
  }

  translateObject<T = unknown>(
    key: string[] | TranslateParams,
    params?: HashMap | null | undefined,
    lang?: string,
  ): T | T[] {
    if (params === null) {
      params = undefined;
    }
    return this.transloco.translateObject<T>(key, params, lang);
  }

  private filterAvailableLanguage(x: string) {
    if (this.conf.getAvailableLanguages().some((language) => language === x)) {
      return x;
    }
    const def = this.conf.getDefaultLanguage();
    logger.log(`language "${x}" not allowed, using default "${def}"`);
    return def;
  }
}
